import { useEffect, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { CheckCircle2, AlertCircle, ArrowRight } from "lucide-react";
import api from "../lib/api";
import Seo from "../components/Seo";

export default function VerifyEmail() {
  const [params] = useSearchParams();
  const token = params.get("token");
  const [state, setState] = useState({ loading: true, ok: false, error: null, email: "" });

  useEffect(() => {
    if (!token) {
      setState({ loading: false, ok: false, error: "Missing verification token", email: "" });
      return;
    }
    (async () => {
      try {
        const { data } = await api.get(`/members/verify?token=${encodeURIComponent(token)}`);
        setState({ loading: false, ok: true, error: null, email: data.email });
      } catch (err) {
        setState({ loading: false, ok: false, error: err.response?.data?.detail || "Verification failed", email: "" });
      }
    })();
  }, [token]);

  return (
    <main id="main-content" className="min-h-screen bg-[#050505] text-white flex items-center justify-center px-5 py-20 relative overflow-hidden" data-testid="verify-page">
      <Seo title="Verify Email" path="/verify" />
      <div className="absolute inset-0 lightning-grid opacity-30" />
      <div className="relative max-w-md w-full text-center">
        <img src="/logo.png" alt="The Push Center" className="w-20 h-20 rounded-full ring-2 ring-electric/40 mb-6 mx-auto" />
        {state.loading ? (
          <div className="py-10">
            <div className="w-12 h-12 border-2 border-electric border-t-transparent rounded-full animate-spin mx-auto mb-4" role="status" aria-label="Verifying" />
            <p className="text-zinc-400">Verifying...</p>
          </div>
        ) : state.ok ? (
          <>
            <div className="w-20 h-20 rounded-full bg-electric/20 flex items-center justify-center mx-auto mb-6 ring-1 ring-electric/40">
              <CheckCircle2 className="w-10 h-10 text-electric" />
            </div>
            <div className="text-[11px] uppercase tracking-[0.4em] text-electric font-semibold mb-3">Verified</div>
            <h1 className="font-display font-black text-3xl lg:text-4xl tracking-tighter mb-4">You're <span className="italic text-electric">in.</span></h1>
            <p className="text-zinc-300 mb-8">Your email <strong className="text-white">{state.email}</strong> is confirmed. Sign in to access your groups.</p>
            <Link to="/login" className="inline-flex items-center gap-2 bg-electric text-black px-7 py-4 rounded-full font-bold hover:bg-[#4ade80] transition" data-testid="verify-go-login">
              Go to Sign In <ArrowRight className="w-4 h-4" />
            </Link>
          </>
        ) : (
          <>
            <div className="w-20 h-20 rounded-full bg-red-500/20 flex items-center justify-center mx-auto mb-6 ring-1 ring-red-500/40">
              <AlertCircle className="w-10 h-10 text-red-400" />
            </div>
            <h1 className="font-display font-black text-3xl tracking-tighter mb-4">Something's off.</h1>
            <p className="text-zinc-300 mb-8" data-testid="verify-error">{state.error}</p>
            <Link to="/signup" className="inline-flex items-center gap-2 border border-white/20 text-white px-7 py-4 rounded-full font-bold hover:border-electric hover:text-electric transition">
              Try again
            </Link>
          </>
        )}
      </div>
    </main>
  );
}
